import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Breadcrumbs } from "@pgs-core/ui/components/breadcrumbs";
import { Sidebar } from "./sidebar.jsx";
import { deriveCrumbs } from "../lib/breadcrumbs";
import type { ApiClient } from "../lib/api";
import type { AdminClient, AdminUser } from "../lib/outlet-context";
import { normalizeClient } from "../lib/clients";

interface AdminShellProps {
  user: AdminUser | null;
  /** Authenticated API client; null when no API is configured. */
  api: ApiClient | null;
  environment: string | null;
  onSignOut: () => void;
  changePassword: (password: string) => Promise<string | null>;
}

export function AdminShell({ user, api, environment, onSignOut, changePassword }: AdminShellProps) {
  const { pathname } = useLocation();
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [schemaLatest, setSchemaLatest] = useState(0);

  useEffect(() => {
    if (!api) return;
    api.request<{ clients?: AdminClient[]; schema_latest?: number }>("/admin/clients")
      .then((data) => {
        setClients((data.clients ?? []).map(normalizeClient));
        setSchemaLatest(data.schema_latest ?? 0);
      })
      .catch(() => {});
  }, [api]);

  const addClient = (rawClient: AdminClient) => {
    setClients((prev) => [normalizeClient(rawClient), ...prev]);
  };

  const updateClient = (rawClient: AdminClient) => {
    setClients((prev) =>
      prev.map((c) => (c.id === rawClient.id ? normalizeClient(rawClient) : c))
    );
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar user={user} environment={environment} onSignOut={onSignOut} />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* The header lives inside the scroll container so it scrolls away with
            the content (a page's sticky tab bar takes its place on scroll). */}
        <main className="flex-1 overflow-y-auto">
          <header className="flex h-14 items-center border-b border-white/10 bg-[var(--color-header)] px-4">
            <Breadcrumbs items={deriveCrumbs(pathname, clients)} />
          </header>

          <div className="p-8">
            <Outlet context={{ clients, addClient, updateClient, api, user, schemaLatest, changePassword }} />
          </div>
        </main>
      </div>
    </div>
  );
}
